import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../utils/api';
import { errorText } from '../../utils/errorText';
import * as S from './styles';

/*
 * An isolate's lab code, and -- for whoever may correct a specimen
 * (specimen:write) while it is active -- a way to set it to the code the
 * bank's tube already carries (migration 0083; PUT /specimens/{id}
 * {lab_code, reason}). The reason is required: the database refuses a change
 * without one, and the specimen's history keeps it with the old and new code.
 *
 * An L-number is written the database's way ("l-42" is saved as L-00042);
 * any other code as typed: letters, digits and . _ / -, up to 40, no spaces.
 * A code another isolate has (ignoring case) is refused, naming it.
 *
 * `onSave(body)` when the page saves through its own handler (SpecimenDetail
 * redraws from the server's answer); else the editor PUTs and calls
 * `onSaved(specimen)`.
 */
export const LAB_CODE_PATTERN = /^[A-Za-z0-9][A-Za-z0-9._/-]{0,39}$/;

export default function LabCodeEditor({ specimenId, labCode, canEdit, onSave, onSaved, compact = false }) {
  const { t } = useTranslation('specimens');
  const [editing, setEditing] = useState(false);
  const [code, setCode] = useState(labCode || '');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const typed = code.trim();
  const valid = LAB_CODE_PATTERN.test(typed);
  const changed = typed !== (labCode || '');
  const ready = valid && changed && reason.trim().length >= 3 && !busy;

  const open = () => {
    setCode(labCode || '');
    setReason('');
    setError('');
    setEditing(true);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!ready) return;
    setBusy(true);
    setError('');
    const body = { lab_code: typed, reason: reason.trim() };
    try {
      if (onSave) {
        const ok = await onSave(body);
        if (ok !== false) setEditing(false);
      } else {
        const res = await api.put(`/specimens/${specimenId}`, body);
        setEditing(false);
        onSaved?.(res.data);
      }
    } catch (err) {
      const data = err?.response?.data || {};
      if (data.code === 'lab_code_taken') setError(t('labCodeEdit.taken', { code: data.lab_code, holder: data.holder }));
      else if (data.code === 'invalid_lab_code') setError(t('labCodeEdit.invalid'));
      else setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  if (!editing) {
    return (
      <span style={{ display: 'inline-flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}
        data-testid="lab-code-editor">
        {!compact && (
          <span title={t('detail.tip.labCode')}>
            {t('fields.labCode')}:{' '}
            <bdi dir="ltr" style={{ ...S.mono, fontWeight: 700 }} data-testid="lab-code-value">{labCode || '—'}</bdi>
          </span>
        )}
        {canEdit && (
          <button type="button" style={S.ghostBtn} onClick={open} title={t('labCodeEdit.tip')}
            data-testid="lab-code-edit">{t('labCodeEdit.button')}</button>
        )}
      </span>
    );
  }

  return (
    <form onSubmit={save} style={{ display: 'grid', gap: 8, maxWidth: 420 }} data-testid="lab-code-form"
      aria-label={t('labCodeEdit.title')}>
      <label style={{ display: 'grid', gap: 4 }}>
        <span>{t('labCodeEdit.code')}</span>
        <input value={code} onChange={(e) => setCode(e.target.value)} maxLength={40} dir="ltr"
          style={{ ...S.input, ...S.mono }} aria-invalid={typed !== '' && !valid} data-testid="lab-code-input" />
      </label>
      <span style={S.hint}>{typed !== '' && !valid ? t('labCodeEdit.invalid') : t('labCodeEdit.hint')}</span>
      <label style={{ display: 'grid', gap: 4 }}>
        <span>{t('labCodeEdit.reason')}</span>
        <input value={reason} onChange={(e) => setReason(e.target.value)} maxLength={2000} required minLength={3}
          style={S.input} dir="auto" data-testid="lab-code-reason" />
      </label>
      {error && <p role="alert" style={{ ...S.error, margin: 0 }} data-testid="lab-code-error">{error}</p>}
      <div style={{ display: 'flex', gap: 8 }}>
        <button type="submit" style={S.btn()} disabled={!ready} data-testid="lab-code-save">
          {busy ? t('labCodeEdit.saving') : t('labCodeEdit.save')}
        </button>
        <button type="button" style={S.ghostBtn} onClick={() => setEditing(false)} disabled={busy}>
          {t('cancel')}
        </button>
      </div>
    </form>
  );
}
